/**
 * @fileoverview Portaled widget properties editor (`CanvasWidgetPropertiesPanel`) with
 * `Content | Style` sub-tabs for `DashboardCanvasBuilder`.
 * @module ui-admin/components/DashboardCanvasBuilder/CanvasWidgetPropertiesPanel
 *
 * Responsibilities: - Edit the selected `DashboardWidget`'s title, `enabled` toggle, `dataSource`
 *                     (`cubeId`, measures, dimensions), type-specific sub-configs (`chartConfig`,
 *                     `kpiConfig`, `tableConfig`, `filterConfig`, `textConfig`, `listConfig`,
 *                     `progressBarConfig`, `dividerConfig`, `imageConfig`, `calendarConfig`),
 *                     grid layout (`w, h`), and card `style` (`backgroundColor`, `borderColor`,
 *                     `borderWidth`, `textAlign`, `headerColor`).
 * Dependencies:     - React, lucide-react
 *                   - @firesquirrel/local-db (DashboardWidget, CatalogCube, REPORT_DATA_CATALOG)
 * Architecture:     - Presentation-only controlled component; portaled into the host's right-pane
 *                     `Properties` tab (`propertiesPortalTarget`) by `DashboardCanvasBuilder`.
 * Notes:            - Closes gaps G-08, G-12, and G-51.
 */

import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { REPORT_DATA_CATALOG } from '@firesquirrel/local-db';
import type { DashboardWidget, CatalogCube, ChartConfig } from '@firesquirrel/local-db';

/**
 * @contract CanvasWidgetPropertiesPanelProps
 * Purpose: Props for `CanvasWidgetPropertiesPanel`.
 * Fields:  widget, onUpdate, onDelete, isReadOnly?, dataCatalog?
 * Rules:   Controlled component; emits partial `DashboardWidget` updates via `onUpdate`.
 * Sync:    N/A.
 */
export interface CanvasWidgetPropertiesPanelProps {
  widget: DashboardWidget | null;
  onUpdate: (updates: Partial<DashboardWidget>) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  isReadOnly?: boolean;
  dataCatalog?: CatalogCube[];
}

const CHART_SUBTYPES: Array<{ value: ChartConfig['type']; label: string }> = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'donut', label: 'Donut Chart' },
  { value: 'radar', label: 'Radar Chart' },
  { value: 'scatter', label: 'Scatter Plot' },
];

/**
 * @component CanvasWidgetPropertiesPanel
 * Purpose:     Edit the selected widget's Content and Style properties in the right-pane Properties tab.
 * Props:       CanvasWidgetPropertiesPanelProps.
 * Behavior:    Provides `Content` and `Style` sub-tabs; writes typed sub-configs onto the selected widget.
 * Data Source: none (controlled; uses `dataCatalog` or `REPORT_DATA_CATALOG` for cube/field pickers).
 * Constraints: presentation-only; no persistence.
 * Performance: stateless except active `propTab` ('content' | 'style').
 */
export function CanvasWidgetPropertiesPanel({
  widget,
  onUpdate,
  onDelete,
  onDuplicate,
  isReadOnly = false,
  dataCatalog = REPORT_DATA_CATALOG,
}: CanvasWidgetPropertiesPanelProps) {
  const [propTab, setPropTab] = useState<'content' | 'style'>('content');

  if (!widget) {
    return (
      <div
        data-testid="widget-properties-empty"
        className="p-6 text-center text-xs text-gray-500"
      >
        Select a widget on the canvas or in the activation list to edit its properties.
      </div>
    );
  }

  const cubes = dataCatalog.length > 0 ? dataCatalog : REPORT_DATA_CATALOG;
  const activeCubeId = widget.dataSource?.cubeId ?? cubes[0]?.id ?? 'InspectionSeries';
  const activeCube = cubes.find((c) => c.id === activeCubeId) ?? cubes[0];

  const handleCubeChange = (cubeId: string) => {
    const cube = cubes.find((c) => c.id === cubeId);
    const defaultMeasure = cube?.measures?.[0]?.name ?? `${cubeId}.count`;
    const defaultDim = cube?.dimensions?.[0]?.name;
    // For filter widgets the target field must belong to the newly selected
    // cube — reset it to that cube's first dimension so a stale cross-cube
    // member (e.g. InspectionSeries.status on a Contacts filter) cannot persist.
    const filterField =
      widget.type === 'filter'
        ? cube?.dimensions?.some((d) => d.name === widget.filterConfig?.field)
          ? widget.filterConfig!.field
          : (defaultDim ?? '')
        : widget.filterConfig?.field;
    onUpdate({
      dataSource: {
        cubeId,
        measures: [defaultMeasure],
        dimensions: defaultDim && (widget.type === 'chart' || widget.type === 'table') ? [defaultDim] : [],
        filters: widget.dataSource?.filters ?? [],
      },
      ...(widget.type === 'filter'
        ? {
            filterConfig: {
              targetWidgets: widget.filterConfig?.targetWidgets ?? [],
              type: widget.filterConfig?.type ?? 'select',
              label: widget.filterConfig?.label ?? widget.title,
              field: filterField ?? '',
              lookupType: widget.filterConfig?.lookupType,
            },
          }
        : {}),
    });
  };

  const handleMeasureChange = (measureName: string) => {
    onUpdate({
      dataSource: {
        cubeId: activeCubeId,
        measures: [measureName],
        dimensions: widget.dataSource?.dimensions ?? [],
        filters: widget.dataSource?.filters ?? [],
      },
      ...(widget.type === 'kpi'
        ? {
            kpiConfig: {
              measure: measureName,
              format: widget.kpiConfig?.format ?? 'number',
              trendDirection: widget.kpiConfig?.trendDirection ?? 'up-good',
            },
          }
        : {}),
    });
  };

  const handleDimensionChange = (dimName: string) => {
    onUpdate({
      dataSource: {
        cubeId: activeCubeId,
        measures: widget.dataSource?.measures ?? [`${activeCubeId}.count`],
        dimensions: dimName ? [dimName] : [],
        filters: widget.dataSource?.filters ?? [],
      },
      ...(widget.type === 'chart'
        ? {
            chartConfig: {
              type: widget.chartConfig?.type ?? 'bar',
              ...widget.chartConfig,
              xAxis: dimName || undefined,
            },
          }
        : {}),
    });
  };

  return (
    <div
      data-testid="canvas-widget-properties-panel"
      className="p-4 space-y-4 text-xs"
    >
      {/* Top header */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600 block">
            {`${widget.type} widget`}
          </span>
          <h3 className="text-sm font-semibold text-gray-900 truncate">{widget.title}</h3>
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            disabled={isReadOnly}
            checked={widget.enabled !== false}
            onChange={(e) => onUpdate({ enabled: e.target.checked })}
            className="rounded border-gray-300 text-orange-600"
          />
          Enabled
        </label>
      </div>

      {/* Content | Style sub-tabs */}
      <div
        role="tablist"
        aria-label="Widget property tabs"
        className="grid grid-cols-2 gap-1 bg-gray-100 p-1 rounded-lg"
      >
        <button
          type="button"
          role="tab"
          aria-selected={propTab === 'content'}
          onClick={() => setPropTab('content')}
          className={`py-1.5 font-semibold rounded-md transition-colors ${
            propTab === 'content' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-600'
          }`}
        >
          Content
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={propTab === 'style'}
          onClick={() => setPropTab('style')}
          className={`py-1.5 font-semibold rounded-md transition-colors ${
            propTab === 'style' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-600'
          }`}
        >
          Style
        </button>
      </div>

      {propTab === 'content' ? (
        <div className="space-y-3">
          {/* Title */}
          <div>
            <label htmlFor="widget-prop-title" className="block font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              id="widget-prop-title"
              type="text"
              disabled={isReadOnly}
              value={widget.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
            />
          </div>

          {/* Data source pickers for data-driven widgets */}
          {widget.type !== 'text' && widget.type !== 'divider' && widget.type !== 'image' && (
            <div className="space-y-2.5 pt-2 border-t border-gray-100">
              <div className="font-semibold text-gray-800">Data Source</div>
              <div>
                <label htmlFor="widget-prop-cube" className="block text-gray-600 mb-1">
                  Collection / Cube
                </label>
                <select
                  id="widget-prop-cube"
                  disabled={isReadOnly}
                  value={activeCubeId}
                  onChange={(e) => handleCubeChange(e.target.value)}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                >
                  {cubes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              {activeCube && activeCube.measures.length > 0 && widget.type !== 'filter' && (
                <div>
                  <label htmlFor="widget-prop-measure" className="block text-gray-600 mb-1">
                    Metric / Measure
                  </label>
                  <select
                    id="widget-prop-measure"
                    disabled={isReadOnly}
                    value={widget.dataSource?.measures?.[0] ?? activeCube.measures[0].name}
                    onChange={(e) => handleMeasureChange(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                  >
                    {activeCube.measures.map((m) => (
                      <option key={m.name} value={m.name}>
                        {`${m.title} (${m.type})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {activeCube &&
                activeCube.dimensions.length > 0 &&
                (widget.type === 'chart' || widget.type === 'table' || widget.type === 'list') && (
                  <div>
                    <label htmlFor="widget-prop-dimension" className="block text-gray-600 mb-1">
                      Group By / Dimension
                    </label>
                    <select
                      id="widget-prop-dimension"
                      disabled={isReadOnly}
                      value={widget.dataSource?.dimensions?.[0] ?? ''}
                      onChange={(e) => handleDimensionChange(e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="">None</option>
                      {activeCube.dimensions.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
            </div>
          )}

          {/* Type-specific sub-config editors */}
          {widget.type === 'chart' && (
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <label htmlFor="widget-prop-chart-type" className="block font-medium text-gray-700">
                Chart Type
              </label>
              <select
                id="widget-prop-chart-type"
                disabled={isReadOnly}
                value={widget.chartConfig?.type ?? 'bar'}
                onChange={(e) =>
                  onUpdate({
                    chartConfig: {
                      ...widget.chartConfig,
                      type: e.target.value as ChartConfig['type'],
                    },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              >
                {CHART_SUBTYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-4 pt-1">
                <label className="inline-flex items-center gap-1.5 text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={isReadOnly}
                    checked={widget.chartConfig?.showLegend ?? true}
                    onChange={(e) =>
                      onUpdate({
                        chartConfig: {
                          type: widget.chartConfig?.type ?? 'bar',
                          ...widget.chartConfig,
                          showLegend: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300 text-orange-600"
                  />
                  Show Legend
                </label>
                <label className="inline-flex items-center gap-1.5 text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={isReadOnly}
                    checked={widget.chartConfig?.showLabels ?? false}
                    onChange={(e) =>
                      onUpdate({
                        chartConfig: {
                          type: widget.chartConfig?.type ?? 'bar',
                          ...widget.chartConfig,
                          showLabels: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300 text-orange-600"
                  />
                  Show Labels
                </label>
              </div>
            </div>
          )}

          {widget.type === 'kpi' && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-gray-600 mb-1">Value Format</label>
                <select
                  disabled={isReadOnly}
                  aria-label="KPI value format"
                  value={widget.kpiConfig?.format ?? 'number'}
                  onChange={(e) =>
                    onUpdate({
                      kpiConfig: {
                        measure: widget.kpiConfig?.measure ?? 'InspectionSeries.count',
                        trendDirection: widget.kpiConfig?.trendDirection ?? 'up-good',
                        ...widget.kpiConfig,
                        format: e.target.value as 'number' | 'currency' | 'percent',
                      },
                    })
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                >
                  <option value="number">Number</option>
                  <option value="currency">Currency ($)</option>
                  <option value="percent">Percent (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Trend Direction</label>
                <select
                  disabled={isReadOnly}
                  aria-label="KPI trend direction"
                  value={widget.kpiConfig?.trendDirection ?? 'up-good'}
                  onChange={(e) =>
                    onUpdate({
                      kpiConfig: {
                        measure: widget.kpiConfig?.measure ?? 'InspectionSeries.count',
                        format: widget.kpiConfig?.format ?? 'number',
                        ...widget.kpiConfig,
                        trendDirection: e.target.value as 'up-good' | 'up-bad',
                      },
                    })
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                >
                  <option value="up-good">Up is Good</option>
                  <option value="up-bad">Up is Bad</option>
                </select>
              </div>
            </div>
          )}

          {widget.type === 'text' && (
            <div className="pt-2 border-t border-gray-100">
              <label htmlFor="widget-prop-text" className="block font-medium text-gray-700 mb-1">
                Content
              </label>
              <textarea
                id="widget-prop-text"
                rows={4}
                disabled={isReadOnly}
                value={widget.textConfig?.content ?? ''}
                onChange={(e) => onUpdate({ textConfig: { content: e.target.value } })}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
          )}

          {widget.type === 'filter' && (
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-gray-600 mb-1">Filter Control Type</label>
                <select
                  disabled={isReadOnly}
                  aria-label="Filter control type"
                  value={widget.filterConfig?.type ?? 'select'}
                  onChange={(e) =>
                    onUpdate({
                      filterConfig: {
                        targetWidgets: widget.filterConfig?.targetWidgets ?? [],
                        field: widget.filterConfig?.field ?? 'InspectionSeries.status',
                        label: widget.filterConfig?.label ?? widget.title,
                        type: e.target.value as 'select' | 'dateRange' | 'text',
                      },
                    })
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                >
                  <option value="select">Dropdown Select</option>
                  <option value="dateRange">Date Range</option>
                  <option value="text">Text Search</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Target Field</label>
                {(() => {
                  const fieldOptions = activeCube?.dimensions ?? [];
                  const storedField = widget.filterConfig?.field;
                  const updateFilterField = (field: string) =>
                    onUpdate({
                      filterConfig: {
                        targetWidgets: widget.filterConfig?.targetWidgets ?? [],
                        type: widget.filterConfig?.type ?? 'select',
                        label: widget.filterConfig?.label ?? widget.title,
                        field,
                        lookupType: widget.filterConfig?.lookupType,
                      },
                    });
                  // Custom-field mode: the stored member is not one of the
                  // selected cube's catalog dimensions (e.g. system
                  // collections like `tenants.status` that the host catalog
                  // does not cover), the cube has no catalog dimensions at
                  // all, or the user explicitly picked "Custom field…".
                  const isCustomField =
                    fieldOptions.length === 0 ||
                    storedField === '' ||
                    (storedField !== undefined &&
                      !fieldOptions.some((d) => d.name === storedField));

                  if (isCustomField) {
                    return (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          disabled={isReadOnly}
                          aria-label="Filter target field"
                          placeholder="e.g. tenants.status"
                          value={storedField ?? ''}
                          onChange={(e) => updateFilterField(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                        />
                        {fieldOptions.length > 0 && (
                          <button
                            type="button"
                            disabled={isReadOnly}
                            title="Pick a catalog field"
                            aria-label="Pick a catalog field"
                            onClick={() => updateFilterField(fieldOptions[0].name)}
                            className="shrink-0 px-2 py-1.5 border border-gray-300 rounded-md bg-white text-gray-600 hover:bg-gray-50"
                          >
                            Catalog
                          </button>
                        )}
                      </div>
                    );
                  }

                  return (
                    <select
                      disabled={isReadOnly}
                      aria-label="Filter target field"
                      value={storedField ?? fieldOptions[0]?.name ?? ''}
                      onChange={(e) => updateFilterField(e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                    >
                      {fieldOptions.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.title}
                        </option>
                      ))}
                      <option value="">Custom field…</option>
                    </select>
                  );
                })()}
              </div>
            </div>
          )}

          {widget.type === 'progressBar' && (
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-gray-600 mb-1">Target Value</label>
              <input
                type="number"
                disabled={isReadOnly}
                aria-label="Progress bar target value"
                value={widget.progressBarConfig?.targetValue ?? 100}
                onChange={(e) =>
                  onUpdate({
                    progressBarConfig: {
                      currentMeasure:
                        widget.progressBarConfig?.currentMeasure ?? 'InspectionSeries.count',
                      format: widget.progressBarConfig?.format ?? 'number',
                      showLabel: widget.progressBarConfig?.showLabel ?? true,
                      ...widget.progressBarConfig,
                      targetValue: Number(e.target.value) || 100,
                    },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
          )}

          {widget.type === 'image' && (
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-gray-600 mb-1">Image URL</label>
              <input
                type="text"
                disabled={isReadOnly}
                aria-label="Image URL"
                value={widget.imageConfig?.src ?? ''}
                onChange={(e) =>
                  onUpdate({
                    imageConfig: {
                      src: e.target.value,
                      fit: widget.imageConfig?.fit ?? 'contain',
                      alt: widget.title,
                    },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
          )}

          {/* Grid span controls */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
            <div>
              <label htmlFor="widget-prop-width" className="block text-gray-600 mb-1">
                Width (cols)
              </label>
              <input
                id="widget-prop-width"
                type="number"
                min={1}
                max={12}
                disabled={isReadOnly}
                value={widget.layout.w}
                onChange={(e) =>
                  onUpdate({
                    layout: { ...widget.layout, w: Math.min(12, Math.max(1, Number(e.target.value) || 1)) },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
            <div>
              <label htmlFor="widget-prop-height" className="block text-gray-600 mb-1">
                Height (rows)
              </label>
              <input
                id="widget-prop-height"
                type="number"
                min={1}
                max={12}
                disabled={isReadOnly}
                value={widget.layout.h}
                onChange={(e) =>
                  onUpdate({
                    layout: { ...widget.layout, h: Math.min(12, Math.max(1, Number(e.target.value) || 1)) },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
            <div>
              <label htmlFor="widget-prop-x" className="block text-gray-600 mb-1">
                X (col 0–11)
              </label>
              <input
                id="widget-prop-x"
                type="number"
                min={0}
                max={11}
                disabled={isReadOnly}
                value={widget.layout.x}
                onChange={(e) =>
                  onUpdate({
                    layout: { ...widget.layout, x: Math.min(11, Math.max(0, Number(e.target.value) || 0)) },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
            <div>
              <label htmlFor="widget-prop-y" className="block text-gray-600 mb-1">
                Y (row)
              </label>
              <input
                id="widget-prop-y"
                type="number"
                min={0}
                disabled={isReadOnly}
                value={widget.layout.y}
                onChange={(e) =>
                  onUpdate({
                    layout: { ...widget.layout, y: Math.max(0, Number(e.target.value) || 0) },
                  })
                }
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="widget-style-tab-content">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-gray-600 mb-1">Card Background</label>
              <input
                type="color"
                disabled={isReadOnly}
                aria-label="Widget background color"
                value={widget.style?.backgroundColor ?? '#ffffff'}
                onChange={(e) =>
                  onUpdate({ style: { ...widget.style, backgroundColor: e.target.value } })
                }
                className="h-8 w-full rounded border border-gray-300 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Border Color</label>
              <input
                type="color"
                disabled={isReadOnly}
                aria-label="Widget border color"
                value={widget.style?.borderColor ?? '#e5e7eb'}
                onChange={(e) =>
                  onUpdate({ style: { ...widget.style, borderColor: e.target.value } })
                }
                className="h-8 w-full rounded border border-gray-300 cursor-pointer"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-gray-600 mb-1">Header Text Color</label>
              <input
                type="color"
                disabled={isReadOnly}
                aria-label="Widget header color"
                value={widget.style?.headerColor ?? '#111827'}
                onChange={(e) =>
                  onUpdate({ style: { ...widget.style, headerColor: e.target.value } })
                }
                className="h-8 w-full rounded border border-gray-300 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Text Alignment</label>
              <select
                disabled={isReadOnly}
                aria-label="Widget text alignment"
                value={widget.style?.textAlign ?? 'left'}
                onChange={(e) =>
                  onUpdate({
                    style: {
                      ...widget.style,
                      textAlign: e.target.value as 'left' | 'center' | 'right',
                    },
                  })
                }
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {!isReadOnly && (
        <div className="pt-3 border-t border-gray-200 flex items-center gap-2">
          {onDuplicate && (
            <button
              type="button"
              data-testid="duplicate-selected-widget-btn"
              onClick={onDuplicate}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Duplicate Widget
            </button>
          )}
          <button
            type="button"
            data-testid="delete-selected-widget-btn"
            onClick={onDelete}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete Widget
          </button>
        </div>
      )}
    </div>
  );
}

export default CanvasWidgetPropertiesPanel;
